import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiResponse } from "types/ApiResponse";
import { ConfigPayload } from "types/config.api";
import { FirebaseConfig } from "utils/getFirebaseConfig";
import {
  billingPreferences,
  fetchFirebaseConfig,
  getConfig,
  manageNotificationSettings,
  updateQuoteInvoiceSettings,
} from "./thunk";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface ConfigStatus {
  fetch: RequestStatus;
  fetchFirebaseConfig: RequestStatus;
  manageQuoteInvoiceSettings: RequestStatus;
  manageBillingPreferences: RequestStatus;
  manageNotificationSettings: RequestStatus;
}

interface ConfigErrors {
  fetch: string | null;
  fetchFirebaseConfig: string | null;
  manageQuoteInvoiceSettings: string | null;
  manageBillingPreferences: string | null;
  manageNotificationSettings: string | null;
}

export interface ConfigState {
  configData: ConfigPayload | null;
  firebaseConfig: FirebaseConfig | null;
  status: ConfigStatus;
  errors: ConfigErrors;
}

export const initialState: ConfigState = {
  configData: null,
  firebaseConfig: null,
  status: {
    fetch: "idle",
    fetchFirebaseConfig: "idle",
    manageQuoteInvoiceSettings: "idle",
    manageBillingPreferences: "idle",
    manageNotificationSettings: "idle",
  },
  errors: {
    fetch: null,
    fetchFirebaseConfig: null,
    manageQuoteInvoiceSettings: null,
    manageBillingPreferences: null,
    manageNotificationSettings: null,
  },
};

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchFirebaseConfig.pending, (state) => {
      state.status.fetchFirebaseConfig = "loading";
      state.errors.fetchFirebaseConfig = null;
    });
    builder.addCase(
      fetchFirebaseConfig.fulfilled,
      (state, action: PayloadAction<FirebaseConfig | null>) => {
        state.status.fetchFirebaseConfig = "succeeded";
        state.firebaseConfig = action.payload;
      },
    );
    builder.addCase(fetchFirebaseConfig.rejected, (state, action) => {
      state.status.fetchFirebaseConfig = "failed";
      state.errors.fetchFirebaseConfig = action.payload || null;
    });

    builder.addCase(getConfig.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
    });
    builder.addCase(
      getConfig.fulfilled,
      (state, action: PayloadAction<ApiResponse<ConfigPayload>>) => {
        state.status.fetch = "succeeded";
        state.configData = action.payload.payload;
      },
    );
    builder.addCase(getConfig.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    builder.addCase(updateQuoteInvoiceSettings.pending, (state) => {
      state.status.manageQuoteInvoiceSettings = "loading";
      state.errors.manageQuoteInvoiceSettings = null;
    });
    builder.addCase(
      updateQuoteInvoiceSettings.fulfilled,
      (state, action: PayloadAction<ApiResponse<ConfigPayload>>) => {
        state.status.manageQuoteInvoiceSettings = "succeeded";
        state.configData = action.payload.payload;
      },
    );
    builder.addCase(updateQuoteInvoiceSettings.rejected, (state, action) => {
      state.status.manageQuoteInvoiceSettings = "failed";
      state.errors.manageQuoteInvoiceSettings = action.payload || null;
    });

    builder.addCase(billingPreferences.pending, (state) => {
      state.status.manageBillingPreferences = "loading";
      state.errors.manageBillingPreferences = null;
    });
    builder.addCase(
      billingPreferences.fulfilled,
      (state, action: PayloadAction<ApiResponse<ConfigPayload>>) => {
        state.status.manageBillingPreferences = "succeeded";
        state.configData = action.payload.payload;
      },
    );
    builder.addCase(billingPreferences.rejected, (state, action) => {
      state.status.manageBillingPreferences = "failed";
      state.errors.manageBillingPreferences = action.payload || null;
    });

    builder.addCase(manageNotificationSettings.pending, (state) => {
      state.status.manageNotificationSettings = "loading";
      state.errors.manageNotificationSettings = null;
    });
    builder.addCase(
      manageNotificationSettings.fulfilled,
      (state, action: PayloadAction<ApiResponse<ConfigPayload>>) => {
        state.status.manageNotificationSettings = "succeeded";
        state.configData = action.payload.payload;
      },
    );
    builder.addCase(manageNotificationSettings.rejected, (state, action) => {
      state.status.manageNotificationSettings = "failed";
      state.errors.manageNotificationSettings = action.payload || null;
    });
  },
});

export default configSlice.reducer;

export const selectConfigState = (state: any) =>
  state.config as typeof initialState;
